import React from 'react';
import { StyleSheetColorType } from '../../shared/types/styleSheetColors.types';
import { ApplicationState } from '../../shared/types/ApplicationState.types';
import { TitlesType } from '../../shared/types/QuoteTitles.types';
import { JourneyType } from '../../shared/types/QuoteJourney.types';
import StyledSVGProps from '../../molecules/StyledSvg/StyledSvg.types';

export interface MinAndMax {
  min: number;
  max: number;
}

export interface QuoteBorrowLimits extends MinAndMax {
  minCommissionPercentage?: number;
  maxCommissionPercentage?: number;
}

export interface QuoteCommissionLimits extends MinAndMax {}

export interface QuoteContainerProps {
  optionsEndpoint?: string;
  borrowLimitsEndpoint?: string;
  customisePricesEndpoint?: string;
  commissionedQuoteEndpoint?: string;
  customiseCommissionedQuoteEndpoint?: string;
  recommendedQuote?: TitlesType;
  recommendedQuoteText?: string;
  onSubmit: (quoteData: SelectedQuoteData) => void;
  texts: {
    [key: string]: string;
  };
  errorSchema?: { template?: string; properties?: { [key: string]: string } };
  currency?: string;
  locale?: string;
  summaryQuoteBlocks?: SummaryQuoteBlock;
  serviceCaller: (args) => Promise<any>;
  navigate: (path) => void;
  applicationState: ApplicationState;
  updateApplicationState: (state: any) => void;
  track?: (eventName: string, props: any) => void;
  tooltipFillColor?: StyleSheetColorType;
  graph?: GraphProps;
  dataCyAttributes?: {
    dataCyQuoteList?: {
      selectQuoteList?: string[];
      customiseQuoteList?: string[];
    };
    dataCyCustomise?: {
      amount?: string;
      split?: string;
      selectQuote?: string;
      cancelQuote?: string;
    };
    dataCyConfirmation?: {
      selectQuote?: string;
      cancelQuote?: string;
    };
    quoteModal?: string;
  };
  journeyType?: JourneyType;
  renewalHeader?: string;
  footer?: string;
  buttonText?: string;
  onStepChange?: (activeStep: number) => void;
  quoteMessagesQuoteList?: React.ReactNode;
  quoteMessagesCustomiseQuote?: React.ReactNode;
  quoteMessagesQuoteAcknowledge?: React.ReactNode;
  hidePopup?: boolean;
  smallerSpacing?: boolean;
}

export interface SummaryQuoteBlock {
  purchaseAmount: SummaryBlockProps;
  split: SummaryBlockProps;
  term: SummaryBlockProps;
  balance?: SummaryBlockProps;
  payAmount?: SummaryBlockProps;
}

export type GraphProps = Omit<StyledSVGProps, 'locale' | 'currency'> & {
  imageUrl?: string;
};

export interface SummaryBlockProps {
  title: string;
  tooltip?: string;
  classNames?: string;
}

export interface Quote {
  split: number;
  purchaseAmount: number;
  purchasePrice: number;
  contractPurchasePrice: number;
  contractPurchaseAmount: number;
  outstandingAmount: number;
  factorRate: number;
  term: number;
  commissionAmount?: number;
  commissionPercentage?: number;
}

export interface QuoteOption extends Quote {
  description?: TitlesType;
  offerType?: OfferType;
}

export enum OfferType {
  APPROVED = 'Approved',
  ELIGIBLE = 'Eligible',
}

export interface QuoteOptions {
  quotes: QuoteOption[];
}

export interface CustomQuote extends QuoteOption {}

export interface SplitLimits extends MinAndMax {}

export interface QuoteService {
  getQuotes: (customerReference: string) => Promise<QuoteOptions>;
  getCustomiseQuotes: (
    customerReference: string,
    amountRequested: number,
    CommissionPercentRequested?: number
  ) => Promise<CustomQuote[]>;
  getBorrowLimits: (
    customerReference: string,
    offerType?: OfferType,
    CommissionPercentRequested?: number
  ) => Promise<QuoteBorrowLimits>;
}

export type QuoteServiceFactory = (
  serviceCaller: (args) => Promise<any>,
  endpoints: {
    optionsEndpoint?: string;
    borrowLimitsEndpoint?: string;
    customisePricesEndpoint?: string;
    customiseCommissionedQuoteEndpoint?: string;
  }
) => QuoteService;

export interface Decision {
  businessRiskScore?: number;
  decisionStatus: number;
}
export interface SelectedQuoteData {
  selectedPrice: QuoteOption;
}

export interface BorrowLimitsRequestData {
  offerType?: OfferType;
  commissionPercentRequested?: number;
}
